import React, { type ReactNode } from "react";
import { Box, SimpleGrid, Text } from "@chakra-ui/react";
import { type Theme } from "contexts/color-theme";
import { useWarehouseCdp } from "contexts/warehouse-cdp-context";
import { TextTitle } from "ui/text";
import { DynamicDiagram } from "./diagram";

function StyledText({ children }: { children: ReactNode }) {
  return (
    <Box as="li">
      <Text as="h3" textStyle={{ base: "body3", md: "body2" }}>
        {children}
      </Text>
    </Box>
  );
}

function Bold({ children }: { children: ReactNode }) {
  return (
    <Text as="span" fontWeight="bold">
      {children}
    </Text>
  );
}

type WarehouseCdpDiagramProps = {
  theme?: Theme;
};

export function WarehouseCdpDiagram({ theme = "light" }: WarehouseCdpDiagramProps) {
  const { warehouse } = useWarehouseCdp();

  return (
    <Box>
      <TextTitle
        align="center"
        theme={theme}
        size="lg"
        maxW="680px"
        title={`Turn ${warehouse.name} into your customer data platform`}
        description="Don’t copy your data to an incompatible and less secure data silo. Turn your existing data into a CDP instead."
      />
      <DynamicDiagram />
      <SimpleGrid
        as="ul"
        columns={{ base: 1, md: 2, lg: 4 }}
        gap={{ base: "small", md: "big", lg: "small" }}
        listStyleType="none"
        mt="bigger"
      >
        <StyledText>
          <Bold>Leverage any data point</Bold> in {warehouse.name} – not just users, accounts, and events
        </StyledText>
        <StyledText>
          <Bold>Build and activate audiences</Bold> directly from {warehouse.name}
        </StyledText>
        <StyledText>
          <Bold>Integrate</Bold> with your existing data infrastructure
        </StyledText>
        <StyledText>
          <Bold>Maintain control over your data</Bold> and eliminate duplicate storage
        </StyledText>
      </SimpleGrid>
    </Box>
  );
}
